import {Component, computed, DOCUMENT, inject, input, InputSignal, OnDestroy, output, OutputEmitterRef, Signal, ChangeDetectionStrategy} from '@angular/core';
import {IconComponent} from '../../icon/icon.component';
import {Icon} from '../../../types/icons/icon.type';
import {TooltipDirective} from '../../../directives/tooltip/tooltip.directive';
import {TooltipPositionEnum} from '../../../enums/tooltip/tooltip-position.enum';
import {TooltipData} from '../../../models/tooltip-data.model';
import {TooltipStyleEnum} from '../../../enums/tooltip/tooltip-style.enum';
import {ButtonTypeEnum} from '../../../enums/button/button-type.enum';
import {DefaultIcon} from '../../../types/icons/default-icon.type';
import {Observable} from 'rxjs';
import {AppButtonTypeEnum} from '../../../enums/button/app-button-type.enum';
import {defaultContextmenuDelay} from '../../../constants/ui/default-contextmenu-delay.constant';
import {ButtonColorEnum} from '../../../enums/button/button-color.enum';
import {NgClass, NgTemplateOutlet} from '@angular/common';

@Component({
    selector: 'app-button',
    imports: [
        IconComponent,
        TooltipDirective,
        NgClass,
        NgTemplateOutlet
    ],
    templateUrl: './button.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './button.component.css',
})
export class ButtonComponent implements OnDestroy {
    public verticalText: InputSignal<boolean> = input(false);
    public appButtonType: InputSignal<AppButtonTypeEnum> = input.required();
    public buttonColor: InputSignal<ButtonColorEnum> = input<ButtonColorEnum>(ButtonColorEnum.CUSTOM);
    public buttonText: InputSignal<string> = input.required();
    public buttonType: InputSignal<ButtonTypeEnum> = input<ButtonTypeEnum>(ButtonTypeEnum.BUTTON);
    public defaultIcon: InputSignal<DefaultIcon | undefined> = input();
    public iconUrl: InputSignal<Observable<string | undefined> | undefined> = input();
    public isDisabled: InputSignal<boolean> = input(false);
    public isLongPressButton: InputSignal<boolean> = input(false);
    public isPulsing: InputSignal<boolean> = input(false);
    public linkUrl: InputSignal<string | undefined> = input();
    public qaTag: InputSignal<string> = input.required();
    public svgIcon: InputSignal<Icon | undefined> = input();
    public tooltipData: InputSignal<TooltipData | undefined> = input<TooltipData>();
    public clicked: OutputEmitterRef<void> = output();
    public longPress: OutputEmitterRef<void> = output();
    protected readonly TooltipPositionEnum = TooltipPositionEnum;
    protected readonly TooltipStyleEnum = TooltipStyleEnum;
    protected readonly AppButtonTypeEnum = AppButtonTypeEnum;
    protected cssClasses: Signal<Array<string>>;
    private longPressTimer: number | undefined;
    private hasReceivedPointerDown: boolean = false;
    private readonly window = inject(DOCUMENT).defaultView ?? window;

    public constructor() {
        this.cssClasses = computed(() => {
            const classes: Array<string> = [];
            const color = this.buttonColor();
            if (color !== ButtonColorEnum.CUSTOM) {
                classes.push('color-' + color);
            }

            return classes;
        });
    }

    public ngOnDestroy(): void {
        this.clearLongPressTimer();
    }

    protected handleButtonClick(event: MouseEvent): void {
        event.stopPropagation();
        if (this.isDisabled()) {
            event.preventDefault();
            return;
        }

        // Ignore ghost clicks on the element. For more info, see: https://amagno.atlassian.net/browse/APP-1808
        const isPointerClick = event.detail > 0;
        if (isPointerClick && !this.hasReceivedPointerDown) {
            return;
        }

        this.hasReceivedPointerDown = false;
        if (!this.isLongPressButton()) {
            this.clicked.emit();
        }
    }

    protected handlePointerDown(event: PointerEvent): void {
        event.stopPropagation();
        this.hasReceivedPointerDown = true;
        if (this.isDisabled() || !this.isLongPressButton() || event.button === 2) {
            return;
        }

        event.preventDefault();
        this.longPressTimer = this.window.setTimeout(() => {
            this.longPress.emit();
            this.longPressTimer = undefined;
        }, defaultContextmenuDelay);
    }

    protected handlePointerUp(event: PointerEvent): void {
        event.stopPropagation();
        if (!this.isLongPressButton() || event.button === 2 || !this.longPressTimer) {
            return;
        }

        this.clearLongPressTimer();
        this.clicked.emit();
    }

    protected handlePointerCancel(): void {
        this.hasReceivedPointerDown = false;
        this.clearLongPressTimer();
    }

    protected handleKeyUp(event: KeyboardEvent): void {
        event.stopPropagation();
        if (this.isDisabled() || !this.isLongPressButton() || (event.key !== 'Enter' && event.key !== ' ')) {
            return;
        }

        this.clearLongPressTimer();
        if (event.shiftKey) {
            this.longPress.emit();
        } else {
            this.clicked.emit();
        }
    }

    private clearLongPressTimer(): void {
        this.window.clearTimeout(this.longPressTimer);
        this.longPressTimer = undefined;
    }
}
